import React, { useEffect } from 'react';
import { useDossier } from './DossierContext';

/**
 * Composant effect-only : écoute Cmd+Z / Ctrl+Z (undo) et
 * Cmd+Y / Ctrl+Y / Cmd+Shift+Z (redo) sur la fenêtre. Rend `null`.
 *
 * Les raccourcis ne s'activent que quand le focus n'est pas dans
 * un input/textarea classique (les contenteditable de l'éditeur,
 * eux, sont gérés différemment et bénéficient des raccourcis).
 */
const DossierKeyboardShortcuts: React.FC = () => {
  const { undo, redo, canUndo, canRedo } = useDossier();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? event.metaKey : event.ctrlKey;
      if (!ctrlOrCmd) return;

      const target = event.target as HTMLElement;
      const isTextField = target.tagName === 'TEXTAREA' || target.tagName === 'INPUT';
      if (isTextField && !target.isContentEditable) return;

      if (event.key === 'z' && !event.shiftKey) {
        if (canUndo) {
          event.preventDefault();
          undo();
        }
        return;
      }

      if (event.key === 'y' || (event.key === 'z' && event.shiftKey)) {
        if (canRedo) {
          event.preventDefault();
          redo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo]);

  return null;
};

export default DossierKeyboardShortcuts;
